import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { FileText, Save, Image, Video, Type, AlignLeft, Upload, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { API_URL as API } from "../../config";
import { uploadFile } from "../../utils/chunkedUpload";

const PAGE_SCHEMAS = {
  home: {
    label: { en: "Home Page", es: "Pagina de Inicio" },
    sections: [
      { key: "hero_title", label: "Hero Title", type: "text" },
      { key: "hero_subtitle", label: "Hero Subtitle", type: "text" },
      { key: "hero_description", label: "Hero Description", type: "textarea" },
      { key: "hero_image", label: "Hero Background Image", type: "image" },
      { key: "hero_video", label: "Hero Video (URL or YouTube)", type: "video" },
      { key: "instagram_url", label: "Instagram URL", type: "text" },
      { key: "gofundme_url", label: "GoFundMe URL", type: "text" },
      { key: "mission_title", label: "Mission Section Title", type: "text" },
      { key: "mission_description", label: "Mission Description", type: "textarea" },
      { key: "training_image", label: "Training Image", type: "image" },
      { key: "huddle_image", label: "Huddle/Team Image", type: "image" },
    ],
  },
  about: {
    label: { en: "About Page", es: "Pagina Acerca de" },
    sections: [
      { key: "hero_title", label: "Page Title", type: "text" },
      { key: "hero_subtitle", label: "Subtitle", type: "text" },
      { key: "story_title", label: "Our Story Title", type: "text" },
      { key: "story_description", label: "Our Story Description", type: "textarea" },
      { key: "story_image", label: "Story Image", type: "image" },
      { key: "provide_title", label: "What We Provide Title", type: "text" },
      { key: "video_url", label: "Featured Video (URL or YouTube)", type: "video" },
    ],
  },
  coaching: {
    label: { en: "Coaching Page", es: "Pagina de Entrenamiento" },
    sections: [
      { key: "hero_title", label: "Page Heading", type: "text" },
      { key: "hero_subtitle", label: "Subtitle / Description", type: "textarea" },
      { key: "hero_badge", label: "Badge Text", type: "text" },
      { key: "hero_image", label: "Hero Image", type: "image" },
      { key: "group_schedule", label: "Group Training Schedule", type: "text" },
      { key: "group_time", label: "Group Training Time", type: "text" },
      { key: "group_ages", label: "Group Training Ages", type: "text" },
      { key: "group_description", label: "Group Training Description", type: "textarea" },
      { key: "video_url", label: "Training Video (URL or YouTube)", type: "video" },
    ],
  },
  events: {
    label: { en: "Events Page", es: "Pagina de Eventos" },
    sections: [
      { key: "hero_title", label: "Page Title", type: "text" },
      { key: "hero_subtitle", label: "Subtitle", type: "text" },
      { key: "hero_description", label: "Description", type: "textarea" },
      { key: "hero_image", label: "Hero Image", type: "image" },
      { key: "video_url", label: "Featured Video (URL or YouTube)", type: "video" },
    ],
  },
  gallery: {
    label: { en: "Gallery Page", es: "Pagina de Galeria" },
    sections: [
      { key: "hero_title", label: "Page Title", type: "text" },
      { key: "hero_subtitle", label: "Subtitle", type: "text" },
      { key: "hero_description", label: "Description", type: "textarea" },
      { key: "featured_video", label: "Featured Video (URL or YouTube)", type: "video" },
    ],
  },
  contact: {
    label: { en: "Contact Page", es: "Pagina de Contacto" },
    sections: [
      { key: "hero_title", label: "Page Title", type: "text" },
      { key: "hero_subtitle", label: "Subtitle", type: "text" },
      { key: "phone", label: "Phone Number", type: "text" },
      { key: "email", label: "Email Address", type: "text" },
      { key: "address", label: "Physical Address", type: "textarea" },
      { key: "map_embed", label: "Google Maps Embed URL", type: "text" },
    ],
  },
};

const PAGES = Object.keys(PAGE_SCHEMAS);

function FieldEditor({ field, value, onChange, onUpload, uploading }) {
  const icon = field.type === "image" ? <Image className="w-4 h-4 text-gray-400" /> : field.type === "video" ? <Video className="w-4 h-4 text-gray-400" /> : field.type === "textarea" ? <AlignLeft className="w-4 h-4 text-gray-400" /> : <Type className="w-4 h-4 text-gray-400" />;

  return (
    <div className="border border-gray-100 rounded-xl p-4 bg-white hover:border-[#87CEEB]/40 transition-colors">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
        {icon} {field.label}
      </label>
      {field.type === "textarea" ? (
        <textarea value={value || ""} onChange={e => onChange(field.key, e.target.value)} rows={3}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#87CEEB] resize-none" />
      ) : field.type === "image" ? (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input type="text" value={value || ""} onChange={e => onChange(field.key, e.target.value)} placeholder="Image URL..."
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#87CEEB]" />
            <label className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${uploading === field.key ? 'bg-gray-100 text-gray-400' : 'bg-[#87CEEB]/10 text-[#87CEEB] hover:bg-[#87CEEB]/20'}`}>
              <Upload className="w-3.5 h-3.5" /> {uploading === field.key ? "..." : "Upload"}
              <input type="file" accept="image/*" className="hidden" disabled={!!uploading} onChange={e => e.target.files[0] && onUpload(field.key, e.target.files[0])} />
            </label>
          </div>
          {value && <img src={value} alt="" className="h-20 rounded-lg object-cover border" />}
        </div>
      ) : field.type === "video" ? (
        <div className="space-y-2">
          <input type="text" value={value || ""} onChange={e => onChange(field.key, e.target.value)} placeholder="YouTube URL or direct video URL..."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#87CEEB]" />
          <label className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${uploading === field.key ? 'bg-gray-100 text-gray-400' : 'bg-violet-50 text-violet-600 hover:bg-violet-100'}`}>
            <Upload className="w-3.5 h-3.5" /> {uploading === field.key ? "Uploading..." : "Upload Video File"}
            <input type="file" accept="video/*" className="hidden" disabled={!!uploading} onChange={e => e.target.files[0] && onUpload(field.key, e.target.files[0])} />
          </label>
          {value && value.includes("youtube") && <p className="text-xs text-gray-400">YouTube embed detected</p>}
        </div>
      ) : (
        <input type="text" value={value || ""} onChange={e => onChange(field.key, e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#87CEEB]" />
      )}
    </div>
  );
}

export default function ContentEditorPage() {
  const { authFetch } = useAuth();
  const { lang } = useLanguage();
  const [activePage, setActivePage] = useState("home");
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [dropOpen, setDropOpen] = useState(false);

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/api/admin/content/${activePage}`);
    if (res.ok) {
      const data = await res.json();
      setContent(data.content || {});
    }
    setSaved(false);
  }, [authFetch, activePage]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleUpload = async (key, file) => {
    setUploading(key);
    try {
      const data = await uploadFile(file, authFetch);
      handleChange(key, data.path);
      toast.success(lang === "es" ? "Archivo subido" : "File uploaded");
    } catch (err) {
      toast.error(lang === "es" ? "Error al subir" : (err.message || "Upload failed"));
    }
    setUploading(null);
  };

  const handleSave = async () => {
    setSaving(true);
    const res = await authFetch(`${API}/api/admin/content/${activePage}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
    });
    if (res.ok) {
      toast.success(lang === "es" ? "Contenido guardado" : "Content saved!");
      setSaved(true);
    } else {
      toast.error(lang === "es" ? "Error al guardar" : "Failed to save");
    }
    setSaving(false);
  };

  const schema = PAGE_SCHEMAS[activePage];

  return (
    <div data-testid="content-editor-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">
            {lang === "es" ? "Contenido del Sitio" : "Website Content"}
          </h2>
          <p className="text-gray-500 text-sm">{lang === "es" ? "Edita el texto, imagenes y videos de cada pagina" : "Edit text, images, and videos for each page"}</p>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-content-btn"
          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all ${saved ? 'bg-emerald-500 text-white' : 'bg-[#87CEEB] text-black hover:bg-[#6bb8d9]'}`}>
          {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saving ? "..." : saved ? (lang === "es" ? "Guardado" : "Saved") : (lang === "es" ? "Guardar Cambios" : "Save Changes")}
        </button>
      </div>

      {/* Page Selector */}
      <div className="relative mb-6">
        <button onClick={() => setDropOpen(!dropOpen)} data-testid="page-selector"
          className="flex items-center justify-between w-full sm:w-72 px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium hover:border-[#87CEEB] transition-colors">
          <span className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#87CEEB]" />
            {schema.label[lang] || schema.label.en}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${dropOpen ? 'rotate-180' : ''}`} />
        </button>
        {dropOpen && (
          <div className="absolute z-20 mt-1 w-full sm:w-72 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
            {PAGES.map(p => (
              <button key={p} onClick={() => { setActivePage(p); setDropOpen(false); }} data-testid={`page-option-${p}`}
                className={`w-full text-left px-4 py-3 text-sm hover:bg-[#87CEEB]/5 transition-colors flex items-center gap-2 ${activePage === p ? 'bg-[#87CEEB]/10 font-semibold text-[#87CEEB]' : 'text-gray-700'}`}>
                <FileText className="w-4 h-4" /> {PAGE_SCHEMAS[p].label[lang] || PAGE_SCHEMAS[p].label.en}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content Fields */}
      <div className="space-y-4">
        {schema.sections.map(field => (
          <FieldEditor key={`${activePage}-${field.key}`} field={field} value={content[field.key]}
            onChange={handleChange} onUpload={handleUpload} uploading={uploading} />
        ))}
      </div>

      {/* Bottom save */}
      {schema.sections.length > 4 && (
        <div className="mt-6 flex justify-end">
          <button onClick={handleSave} disabled={saving}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all ${saved ? 'bg-emerald-500 text-white' : 'bg-[#87CEEB] text-black hover:bg-[#6bb8d9]'}`}>
            {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {saving ? "..." : saved ? (lang === "es" ? "Guardado" : "Saved") : (lang === "es" ? "Guardar" : "Save")}
          </button>
        </div>
      )}
    </div>
  );
}
